import { FastField, Field, Formik } from 'formik';
import KTFormGroup from 'general/components/OtherKeenComponents/Forms/KTFormGroup';
import KTFormInput, {
  KTFormInputType,
} from 'general/components/OtherKeenComponents/Forms/KTFormInput';
import KTCheckbox, {
  KTCheckboxStyle,
  KTCheckboxType,
} from 'general/components/OtherKeenComponents/KTFormControls/KTCheckbox';
import KTRadio, {
  KTRadioType,
} from 'general/components/OtherKeenComponents/KTFormControls/KTRadio';
import GoogleRecaptchaForm from 'general/components/Others/GoogleRecaptchaForm';
import InputSearchDropdown from 'general/components/Others/InputSearchDropdown';
import AppColors from 'general/constants/AppColors';
import AppConfigs from 'general/constants/AppConfigs';
import AppConstants from 'general/constants/AppConstants';
import AppRoute from 'general/constants/AppRoute';
import ToastHelper from 'general/helpers/ToastHelper';
import UIHelper from 'general/helpers/UIHelper';
import miscHelper from 'general/helpers/miscHelper';
import feApi from 'api/feApi';
import miscApi from 'api/miscApi';
import StringUtils from 'general/utils/StringUtils';
import Utils from 'general/utils/Utils';
import useAppConfig from 'hooks/useAppConfig';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import * as Yup from 'yup';

SignUpForm.propTypes = {
  role: PropTypes.string,
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
  isSaleOnly: PropTypes.bool,
};

SignUpForm.defaultProps = {
  role: '',
  onSubmit: null,
  onCancel: null,
  isSaleOnly: false,
};

/**
 *
 * @param {{
 * role: string,
 * onSubmit: function,
 * onCancel: function,
 * isSaleOnly: boolean,
 * }} props
 * @returns
 */
function SignUpForm(props) {
  // MARK: --- Params ---
  const { t } = useTranslation();
  const { role, onSubmit, onCancel, isSaleOnly } = props;
  const currentHost = typeof window !== 'undefined' ? window.location.hostname : '';
  const skipCaptchaHosts = ['localhost', '127.0.0.1', '0.0.0.0', 'iclever.vn', 'evgroup.com.vn'];
  const shouldUseCaptcha =
    Boolean(process.env.REACT_APP_GOOGLE_RECAPTCHA_V2_SITE_KEY) &&
    !skipCaptchaHosts.some((domain) => currentHost === domain || currentHost.endsWith('.' + domain));
  const [isCheckCaptcha, setCheckCaptcha] = useState(!shouldUseCaptcha);
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const [isRoleSale, setRoleSale] = useState(false);
  const [searchParams] = useSearchParams();
  const referralCode = searchParams.get('referralCode') || searchParams.get('ref');
  const [isAgencyIndividual, setAgencyIndividual] = useState(true);
  const termsUrl = useAppConfig()?.termsUrl;
  const [bankList, setBankList] = useState([]);
  const [qBank, setQBank] = useState('');
  const fallbackBanks = [
    { bankCode: 'VCB', shortName: 'Vietcombank', bankName: 'Ngân hàng TMCP Ngoại thương Việt Nam' },
    { bankCode: 'BIDV', shortName: 'BIDV', bankName: 'Ngân hàng TMCP Đầu tư và Phát triển Việt Nam' },
    { bankCode: 'CTG', shortName: 'VietinBank', bankName: 'Ngân hàng TMCP Công thương Việt Nam' },
    { bankCode: 'TCB', shortName: 'Techcombank', bankName: 'Ngân hàng TMCP Kỹ thương Việt Nam' },
    { bankCode: 'MB', shortName: 'MB Bank', bankName: 'Ngân hàng TMCP Quân đội' },
    { bankCode: 'ACB', shortName: 'ACB', bankName: 'Ngân hàng TMCP Á Châu' },
    { bankCode: 'VPB', shortName: 'VPBank', bankName: 'Ngân hàng TMCP Việt Nam Thịnh Vượng' },
    { bankCode: 'TPB', shortName: 'TPBank', bankName: 'Ngân hàng TMCP Tiên Phong' },
  ];

  useEffect(() => {
    const handleGetBankList = async () => {
      try {
        let banks = await miscHelper.callApiGetBankList();
        if (!banks.length) {
          const res = await miscApi.getBankList();
          banks = res?.code === '00' && Array.isArray(res?.data) ? res.data : [];
        }
        const list = banks.map((bank) => ({
          bankCode: bank.code || bank.bankCode,
          shortName: bank.shortName || bank.name || bank.bankName,
          bankName: bank.name || bank.bankName || bank.shortName,
        }));
        setBankList(list.filter((bank) => bank.bankCode).length ? list : fallbackBanks);
      } catch (error) {
        console.error('Error fetching banks:', error);
        setBankList(fallbackBanks);
      }
    };
    handleGetBankList();
  }, []);

  const bankOptions = useMemo(() => {
    if (!bankList || !Array.isArray(bankList)) {
        return [];
    }
    const filteredOptions = qBank
        ? bankList.filter((item) => {
            const bankCode = StringUtils.removeVietnameseTones(item?.bankCode ?? '').toLowerCase();
            const shortName = StringUtils.removeVietnameseTones(item?.shortName ?? '').toLowerCase();
            const bankName = StringUtils.removeVietnameseTones(item?.bankName ?? '').toLowerCase();
            const searchTerm = StringUtils.removeVietnameseTones(qBank ?? '').toLowerCase();
            return bankCode.includes(searchTerm) || shortName.includes(searchTerm) || bankName.includes(searchTerm);
        })
        : bankList;
    return filteredOptions.map((row) => ({
        title: row?.shortName?.length ? `${row?.shortName ?? ''}, ${row?.bankCode ?? ''}` : row?.bankCode ?? '',
        subTitle: row?.bankName ?? '',
        value: row?.bankCode ?? ''
    }));
  }, [bankList, qBank]);

  useEffect(() => {
    setRoleSale(role === 'SALE');
  }, [role]);

  // MARK: --- Functions ---
  function handleSubmit(values) {
    const isCustomer = values.accountRole === AppConstants.userRole.driver;
    const bankName = values.bankCode ? bankList?.find((bank) => bank.bankCode === values.bankCode)?.bankName : '';
    const copiedValues = isCustomer
      ? {
        email: values.email,
        password: values.password,
        fullname: values.fullname,
        phone: values.phone,
        referralCode: values.referralCode,
        primaryRole: AppConstants.userRole.driver,
        registrationSource: 'AFFILIATE_WEB',
        bankCode: values.bankCode || null,
        bankAccount: values.bankAccount || null,
        bankBeneficiary: values.bankBeneficiary || null,
        bankName: bankName || null,
      }
      : {
        ...values,
        bankName: bankName,
        primaryRole: values.requestedCommissionModes,
        requestedCommissionModes: values.requestedCommissionModes
          ? [values.requestedCommissionModes]
          : [],
      };
    delete copiedValues['accountRole'];
    delete copiedValues['acceptTerms'];
    delete copiedValues['confirmPassword'];
    if (recaptchaToken) {
      copiedValues.recaptchaToken = recaptchaToken;
    }
    if (onSubmit) {
      onSubmit(Utils.transformEmptyStrings(copiedValues));
    }
  }

  function handleCancel() {
    if (onCancel) {
      onCancel();
    }
  }

  async function validateReferralCode(value) {
    const normalizedCode = value == null ? '' : String(value).trim();
    if (!normalizedCode) {
      return undefined;
    }

    try {
      const response = await feApi.checkReferralCode(normalizedCode);
      if (response?.result === 'success') {
        return undefined;
      }
      return response?.reason || 'Mã giới thiệu không hợp lệ';
    } catch (error) {
      return error?.response?.data?.reason || 'Mã giới thiệu không hợp lệ';
    }
  }

  return (
    <>
      <div
        className="login-form"
        initial={{ y: 10, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -10, opacity: 0 }}
        transition={{ duration: 0.2 }}
      >
        {/* form */}
        <Formik
          initialValues={{
            operatorId: 1,
            phone: '',
            type: AppConstants.accountType.individual,
            email: '',
            fullname: '',
            name: '',
            password: '',
            confirmPassword: '',
            acceptTerms: true,
            accountRole: AppConstants.userRole.driver,
            requestedCommissionModes: 'GROUP',
            referralCode: referralCode ?? '',
            taxCode: '',
            bankCode: '',
            bankAccount: '',
            bankBeneficiary: '',
          }}
          validationSchema={Yup.object({
            fullname: Yup.string()
              .required('Bạn chưa nhập tên')
              .test('len', t('invalid', { field: 'fullname' }), (val) => {
                if (val) {
                  return val.toString().trim();
                }
              }),
            email: Yup.string()
              .required(t('emailRequired'))
              .test('len', t('Email không hợp lệ', { field: 'email' }), (val) => {
                if (val) {
                  const trimmedVal = val.toString().trim();
                  return (
                    !trimmedVal.includes(' ') && trimmedVal.match(AppConfigs.regex.email)
                  );
                }
                return false;
              }),
            phone: Yup.string()
              .required('Bạn chưa nhập số điện thoại')
              .test('len', t('invalid', { field: 'phone' }), (val) => {
                if (val) {
                  return (
                    !val.toString().includes(' ') && val.toString().match(AppConfigs.regex.phone)
                  );
                }
              }),
            taxCode: Yup.string().when('type', {
              is: (type) => type === 'ORGANIZATION',
              then: (schema) =>
                schema
                  .required(t('required'))
                  .test('len', t('Mã số thuế không hợp lệ', { field: 'taxCode' }), (val) => {
                    if (val) {
                      return (
                        !val.toString().includes(' ') &&
                        (val.toString().match(AppConfigs.regex.taxCode10) ||
                          val.toString().match(AppConfigs.regex.taxCode13))
                      );
                    }
                  }),
              otherwise: (schema) => schema.notRequired(),
            }),
            password: Yup.string()
              .required('Bạn chưa nhập mật khẩu')
              .min(6, 'Mật khẩu phải có ít nhất 6 ký tự'),
            confirmPassword: Yup.string()
              .required('Bạn chưa nhập mật khẩu')
              .oneOf([Yup.ref('password')], 'Mật khẩu không khớp')
              .min(6, 'Mật khẩu phải có ít nhất 6 ký tự'),
            bankCode: Yup.string().when('accountRole', {
              is: (role) => role !== AppConstants.userRole.driver,
              then: (schema) => schema.required(t('required')),
              otherwise: (schema) => schema.notRequired(),
            }),
            bankAccount: Yup.string().when('accountRole', {
              is: (role) => role !== AppConstants.userRole.driver,
              then: (schema) => schema.required(t('required')),
              otherwise: (schema) => schema.notRequired(),
            }),
            bankBeneficiary: Yup.string().when('accountRole', {
              is: (role) => role !== AppConstants.userRole.driver,
              then: (schema) => schema.required(t('required')),
              otherwise: (schema) => schema.notRequired(),
            }),
            acceptTerms: Yup.boolean().test(
              'isTrue',
              'Vui lòng đồng ý với điều khoản trước khi tiếp tục.',
              (value) => value === true
            ),
          })}
          onSubmit={(values) => {
            if (shouldUseCaptcha && !isCheckCaptcha) {
              ToastHelper.showError('Vui lòng xác thực mã captcha');
            } else if (
              values.accountRole !== AppConstants.userRole.driver &&
              values.requestedCommissionModes.length < 1
            ) {
              ToastHelper.showError('Cần chọn ít nhất một vai trò cho tài khoản');
            } else {
              handleSubmit(values);
            }
          }}
        >
          {(formikProps) => (
            <form
              className="form fv-plugins-bootstrap fv-plugins-framework"
              onSubmit={formikProps.handleSubmit}
              noValidate
              autoComplete="off"
            >
              <div className="row">
                {/* title */}
                <div className="pb-5 pt-lg-5 pt-8 text-center">
                  <h3
                    className="font-weight-boldest"
                    style={{
                      fontSize: Utils.returnFontSize(30),
                      color: AppColors.gray[900],
                    }}
                  >
                    {t('Đăng ký tài khoản')}
                  </h3>
                  <span
                    className="text-muted font-weight-bolder"
                    style={{
                      fontSize: Utils.returnFontSize(16),
                    }}
                  >
                    {t('inputDetailInfoToSignUp')}
                  </span>
                </div>
                {!isSaleOnly && (
                  <div className={formikProps.values.accountRole === AppConstants.userRole.driver ? 'd-none' : ''}>
                    <KTFormGroup
                      label={<span className="font-weight-bolder">{t('role')}</span>}
                      inputName="requestedCommissionModes"
                      additionalClassName="fv-plugins-icon-container mb-3"
                      additionalLabelClassName="mb-0 font-size-h6 font-weight-bolder text-gray-700 mb-2"
                      inputElement={
                        <div className="radio-inline gap-5">
                          <Field name="requestedCommissionModes">
                            {({ field, form, meta }) => {
                              const roles = ['GROUP', 'INVESTOR'];

                              return (
                                <div className={'d-flex gap-5'}>
                                  {roles.map((role, index) => (
                                    <div key={role}>
                                      <KTRadio
                                        name={`${field.name}-${role}`}
                                        checked={field.value == role}
                                        label={t(
                                          `agencyRole_${AppConstants.userAgencyType[role.toLowerCase()]
                                          }`
                                        )}
                                        type={KTRadioType.outline}
                                        additionalClassName="radio-success"
                                        onChange={(value) => {
                                          form.setFieldValue(field.name, role);
                                        }}
                                        enableCheckValid
                                        isValid={_.isEmpty(meta.error)}
                                        isTouched={meta.touched}
                                        feedbackText={meta.error}
                                        disabled={role === 'INVESTOR'}
                                      />
                                    </div>
                                  ))}
                                </div>
                              );
                            }}
                          </Field>
                        </div>
                      }
                    />
                  </div>
                )}
                <div className={formikProps.values.accountRole === AppConstants.userRole.driver ? 'd-none' : ''}>
                  <KTFormGroup
                    label={
                      <span className="font-weight-bolder">
                        {formikProps?.values?.requestedCommissionModes === 'SALE'
                          ? 'Loại đại lý'
                          : 'Loại nhà đầu tư'}
                      </span>
                    }
                    inputName="type"
                    additionalClassName="fv-plugins-icon-container mb-2"
                    additionalLabelClassName="mb-0 font-size-h6 font-weight-bolder text-gray-700 mb-2"
                    inputElement={
                      <div className="radio-inline w-100">
                        <Field name="type">
                          {({ field, form, meta }) => (
                            <div className="w-100 d-flex gap-5">
                              <div className="">
                                <KTRadio
                                  name={field.name + 'INDIVIDUAL'}
                                  checked={field.value == 'INDIVIDUAL'}
                                  label={t(`accountType_${AppConstants.accountType.individual}`)}
                                  type={KTRadioType.outline}
                                  additionalClassName="radio-success"
                                  onChange={(value) => {
                                    form.setFieldValue(field.name, 'INDIVIDUAL');
                                    setAgencyIndividual(true);
                                  }}
                                  enableCheckValid
                                  isValid={_.isEmpty(meta.error)}
                                  isTouched={meta.touched}
                                  feedbackText={meta.error}
                                />
                              </div>
                              <div className="">
                                <KTRadio
                                  name={field.name + 'ORGANIZATION'}
                                  checked={field.value == 'ORGANIZATION'}
                                  label={t(`accountType_${AppConstants.accountType.organization}`)}
                                  type={KTRadioType.outline}
                                  additionalClassName="radio-success"
                                  onChange={(value) => {
                                    form.setFieldValue(field.name, 'ORGANIZATION');
                                    setAgencyIndividual(false);
                                  }}
                                  enableCheckValid
                                  isValid={_.isEmpty(meta.error)}
                                  isTouched={meta.touched}
                                  feedbackText={meta.error}
                                />
                              </div>
                            </div>
                          )}
                        </Field>
                      </div>
                    }
                  />
                </div>
                <div className="col-12">
                  <div
                    className="mb-5"
                    style={{
                      borderBottom: `1px dashed ${AppColors.gray[300]}`,
                    }}
                  />
                </div>

                <div className={isAgencyIndividual ? 'col-12 col-md-6 pr-md-2' : 'col-12'}>
                  <KTFormGroup
                    isRequired={true}
                    label={isAgencyIndividual ? t('fullname') : t('Tên tổ chức/doanh nghiệp')}
                    inputName="fullname"
                    additionalClassName="fv-plugins-icon-container mb-4"
                    additionalLabelClassName="mb-0 font-size-h6 font-weight-bolder text-gray-700"
                    inputElement={
                      <Field name="fullname">
                        {({ field, form, meta }) => (
                          <KTFormInput
                            name={field.name}
                            value={field.value}
                            onChange={(value) => {
                              form.setFieldValue(field.name, value);
                              form.setFieldValue('name', value);
                            }}
                            onBlur={() => {
                              form.setFieldTouched(field.name, true);
                            }}
                            enableCheckValid
                            isValid={_.isEmpty(meta.error)}
                            isTouched={meta.touched}
                            feedbackText={meta.error}
                            placeholder={
                              isAgencyIndividual
                                ? t('enterFullname')
                                : t('Nhập tên tổ chức/doanh nghiệp')
                            }
                            type={KTFormInputType.text}
                            solidBackground
                          />
                        )}
                      </Field>
                    }
                  />
                </div>

                {!isAgencyIndividual && formikProps.values.accountRole !== AppConstants.userRole.driver && (
                  <div className="col-12 col-md-6 pr-md-2">
                    <KTFormGroup
                      isRequired={true}
                      label={t('Mã số thuế doanh nghiệp')}
                      inputName="taxCode"
                      additionalClassName="fv-plugins-icon-container mb-4"
                      additionalLabelClassName="mb-0 font-size-h6 font-weight-bolder text-gray-700"
                      inputElement={
                        <Field name="taxCode">
                          {({ field, form, meta }) => (
                            <KTFormInput
                              name={field.name}
                              value={field.value}
                              onChange={(value) => {
                                form.setFieldValue(field.name, value);
                              }}
                              onBlur={() => {
                                form.setFieldTouched(field.name, true);
                              }}
                              enableCheckValid
                              isValid={_.isEmpty(meta.error)}
                              isTouched={meta.touched}
                              feedbackText={meta.error}
                              placeholder={t('Nhập mã số thuế')}
                              type={KTFormInputType.text}
                              solidBackground
                            />
                          )}
                        </Field>
                      }
                    />
                  </div>
                )}

                <div className="col-12 col-md-6 pl-md-2">
                  <KTFormGroup
                    isRequired={true}
                    label={t('phone')}
                    inputName="phone"
                    additionalClassName="fv-plugins-icon-container mb-4"
                    additionalLabelClassName="mb-0 font-size-h6 font-weight-bolder text-gray-700"
                    inputElement={
                      <Field name="phone">
                        {({ field, form, meta }) => (
                          <KTFormInput
                            name={field.name}
                            value={field.value}
                            onChange={(value) => {
                              form.setFieldValue(field.name, value);
                            }}
                            onBlur={() => {
                              form.setFieldTouched(field.name, true);
                            }}
                            enableCheckValid
                            isValid={_.isEmpty(meta.error)}
                            isTouched={meta.touched}
                            feedbackText={meta.error}
                            placeholder={t('enterPhoneNumber')}
                            type={KTFormInputType.text}
                            solidBackground
                          />
                        )}
                      </Field>
                    }
                  />
                </div>

                <div className="col-12 col-md-6 pr-md-2">
                  <KTFormGroup
                    isRequired={true}
                    label={t('email')}
                    inputName="email"
                    additionalClassName="fv-plugins-icon-container mb-4"
                    additionalLabelClassName="mb-0 font-size-h6 font-weight-bolder text-gray-700"
                    inputElement={
                      <Field name="email">
                        {({ field, form, meta }) => (
                          <KTFormInput
                            name={field.name}
                            value={field.value}
                            onChange={(value) => {
                              form.setFieldValue(field.name, value.trim());
                            }}
                            onBlur={() => {
                              form.setFieldTouched(field.name, true);
                            }}
                            enableCheckValid
                            isValid={_.isEmpty(meta.error)}
                            isTouched={meta.touched}
                            feedbackText={meta.error}
                            placeholder="Nhập email"
                            type={KTFormInputType.email}
                            solidBackground
                          />
                        )}
                      </Field>
                    }
                  />
                </div>

                <div className="col-12 col-md-6 pl-md-2">
                  <KTFormGroup
                    label={t('referralCode')}
                    inputName="referralCode"
                    additionalClassName="fv-plugins-icon-container mb-4"
                    additionalLabelClassName="mb-0 font-size-h6 font-weight-bolder text-gray-700"
                    inputElement={
                      <Field name="referralCode" validate={validateReferralCode}>
                        {({ field, form, meta }) => (
                          <KTFormInput
                            name={field.name}
                            value={field.value}
                            onChange={(value) => {
                              form.setFieldValue(field.name, value);
                              if (meta.error) {
                                form.setFieldError(field.name, undefined);
                              }
                            }}
                            onBlur={() => {
                              form.setFieldTouched(field.name, true);
                              form.validateField(field.name);
                            }}
                            enableCheckValid
                            isValid={_.isEmpty(meta.error)}
                            isTouched={meta.touched}
                            feedbackText={meta.error}
                            placeholder={t('Nhập mã giới thiệu')}
                            type={KTFormInputType.text}
                            solidBackground
                            disabled={referralCode}
                          />
                        )}
                      </Field>
                    }
                  />
                </div>

                <div className="col-12 col-md-6 pr-md-2">
                  <KTFormGroup
                    isRequired={true}
                    label={t('password')}
                    inputName="password"
                    additionalClassName="fv-plugins-icon-container mb-4"
                    additionalLabelClassName="mb-0 font-size-h6 font-weight-bolder text-gray-700"
                    inputElement={
                      <Field name="password">
                        {({ field, form, meta }) => (
                          <KTFormInput
                            name={field.name}
                            value={field.value}
                            onChange={(value) => {
                              form.setFieldValue(field.name, value);
                            }}
                            onBlur={() => {
                              form.setFieldTouched(field.name, true);
                            }}
                            enableCheckValid
                            isValid={_.isEmpty(meta.error)}
                            isTouched={meta.touched}
                            feedbackText={meta.error}
                            placeholder={t('enterPassword')}
                            type={KTFormInputType.customPassword}
                            solidBackground
                          />
                        )}
                      </Field>
                    }
                  />
                </div>

                <div className="col-12 col-md-6 pl-md-2">
                  <KTFormGroup
                    isRequired={true}
                    label={t('confirmPassword')}
                    inputName="confirmPassword"
                    additionalClassName="fv-plugins-icon-container mb-4"
                    additionalLabelClassName="mb-0 font-size-h6 font-weight-bolder text-gray-700"
                    inputElement={
                      <Field name="confirmPassword">
                        {({ field, form, meta }) => (
                          <KTFormInput
                            name={field.name}
                            value={field.value}
                            onChange={(value) => {
                              form.setFieldValue(field.name, value);
                            }}
                            onBlur={() => {
                              form.setFieldTouched(field.name, true);
                            }}
                            enableCheckValid
                            isValid={_.isEmpty(meta.error)}
                            isTouched={meta.touched}
                            feedbackText={meta.error}
                            placeholder={t('confirmPassword')}
                            type={KTFormInputType.customPassword}
                            solidBackground
                          />
                        )}
                      </Field>
                    }
                  />
                </div>
                <div className="col-12">
                  <div
                    className="mb-5"
                    style={{
                      borderBottom: `1px dashed ${AppColors.gray[300]}`,
                    }}
                  />
                </div>
              </div>
              {formikProps.values.accountRole !== AppConstants.userRole.driver && (
                <div className="row mb-5">
                   {/* Bank Code */}
                   <div className="col-12">
                      <KTFormGroup
                          label={<span className="font-weight-bolder" style={{ color: AppColors.gray[800] }}>{t('Ngân hàng')}</span>}
                          inputName="bankCode"
                          additionalClassName="mb-4"
                          inputElement={
                              <Field name="bankCode">
                                  {({ field, form, meta }) => {
                                      const selectedBank = bankOptions?.find(bank => bank.value === field.value);
                                      return (
                                          <InputSearchDropdown
                                              inputName={field.name}
                                              selectedValue={field.value}
                                              onSelectedValueChanged={(value) => {
                                                  form.setFieldValue(field.name, value);
                                                  setQBank('');
                                              }}
                                              items={bankOptions}
                                              onSubmitSearch={(text) => {
                                                  setQBank(text);
                                              }}
                                              selectedText={selectedBank ? `${selectedBank?.subTitle} (${selectedBank?.value})` : qBank}
                                              enableCheckValid={true}
                                              isTouched={meta.touched}
                                              isValid={_.isEmpty(meta.error)}
                                              onBlur={() => {
                                                  form.setFieldTouched(field.name, true);
                                              }}
                                              placeholder='Theo tên ngân hàng, tên rút gọn'
                                          />
                                      );
                                  }}
                              </Field>
                          }
                          isRequired={true}
                      />
                   </div>
                   {/* Bank Account */}
                   <div className="col-12 col-md-6 pr-md-2">
                      <KTFormGroup
                          label={<span className="font-weight-bolder" style={{ color: AppColors.gray[800] }}>{t('Số tài khoản ngân hàng')}</span>}
                          inputName="bankAccount"
                          additionalClassName="mb-4"
                          inputElement={
                              <Field name="bankAccount">
                                  {({ field, form, meta }) => {
                                      return (
                                          <KTFormInput
                                              name={field.name}
                                              value={field.value}
                                              onChange={(value) => {
                                                  form.setFieldValue(field.name, value);
                                              }}
                                              onBlur={() => {
                                                  form.setFieldTouched(field.name, true);
                                              }}
                                              enableCheckValid
                                              type={KTFormInputType.text}
                                              placeholder={t('Nhập số tài khoản')}
                                              isValid={_.isEmpty(meta.error)}
                                              isTouched={meta.touched}
                                              feedbackText={meta.error}
                                          />
                                      );
                                  }}
                              </Field>
                          }
                          isRequired={true}
                      />
                   </div>
                   {/* Bank Beneficiary */}
                   <div className="col-12 col-md-6 pl-md-2">
                      <KTFormGroup
                          label={<span className="font-weight-bolder" style={{ color: AppColors.gray[800] }}>{t('Tên chủ tài khoản')}</span>}
                          inputName="bankBeneficiary"
                          additionalClassName="mb-4"
                          inputElement={
                              <FastField name="bankBeneficiary">
                                  {({ field, form, meta }) => {
                                      return (
                                          <KTFormInput
                                              name={field.name}
                                              value={field.value}
                                              onChange={(value) => {
                                                  let filteredValue = StringUtils.removeVietnameseTones(value);
                                                  filteredValue = filteredValue.toUpperCase();
                                                  filteredValue = filteredValue.replace(/[^A-Z0-9 ,.-]/g, '');
                                                  form.setFieldValue(field.name, filteredValue);
                                              }}
                                              onBlur={() => {
                                                  form.setFieldTouched(field.name, true);
                                              }}
                                              enableCheckValid
                                              type={KTFormInputType.text}
                                              placeholder={t('Nhập họ và tên IN HOA, không dấu')}
                                              isValid={_.isEmpty(meta.error)}
                                              isTouched={meta.touched}
                                              feedbackText={meta.error}
                                          />
                                      );
                                  }}
                              </FastField>
                          }
                          isRequired={true}
                      />
                   </div>
                </div>
              )}
              <div className="checkbox-list form-group mb-5">
                <Field name="acceptTerms">
                  {({ field, form, meta }) => (
                    <KTCheckbox
                      additionalClassName="checkbox-success"
                      name={field.name}
                      checked={field.value}
                      onChange={(value) => {
                        form.setFieldValue(field.name, value);
                      }}
                      onBlur={() => {
                        form.setFieldTouched(field.name, true);
                      }}
                      enableCheckValid
                      isValid={_.isEmpty(meta.error)}
                      isTouched={meta.touched}
                      feedbackText={meta.error}
                      label={
                        <div>
                          <span
                            className="font-weight-bold mr-0"
                            style={{ color: AppColors.gray[600] }}
                          >
                            {t('iAgreeThe')}{' '}
                          </span>
                          <span
                            style={UIHelper.buildFontStyle('600', '14px', AppColors.primary)}
                            className="cursor-pointer hover-opacity-60"
                            onClick={() => {
                              Utils.openInNewTab(termsUrl);
                            }}
                          >
                            {t('termsAndConditions').toLowerCase()}
                          </span>
                        </div>
                      }
                      style={KTCheckboxStyle.rounded}
                      type={KTCheckboxType.default}
                    />
                  )}
                </Field>
              </div>

              {shouldUseCaptcha && (
                <div className="form-group mb-2">
                  <GoogleRecaptchaForm
                    onSuccess={(token) => {
                      setCheckCaptcha(true);
                      setRecaptchaToken(token || '');
                    }}
                    onError={() => {
                      setCheckCaptcha(false);
                      setRecaptchaToken('');
                    }}
                    onExpired={() => {
                      setCheckCaptcha(false);
                      setRecaptchaToken('');
                    }}
                  />
                </div>
              )}

              {/* action */}
              <div className="form-group d-flex flex-wrap pb-lg-0 mb-0">
                <button
                  type="submit"
                  className={`btn btn-success font-weight-bolder font-size-h6 px-8 py-4 my-3 flex-grow-1 d-flex align-items-center justify-content-center ${formikProps.values.acceptTerms ? '' : 'disabled'
                    }`}
                >
                  {t('signUp')}
                </button>
              </div>

              <div className="text-center pb-7">
                <span style={UIHelper.buildFontStyle('400', '14px', AppColors.gray[600])}>
                  Đã có tài khoản?
                </span>
                <Link
                  to={`${AppRoute.auth}/sign-in`}
                  className="ml-1 hover-opacity-60"
                  style={UIHelper.buildFontStyle('700', '14px', AppColors.primary)}
                >
                  Đăng nhập
                </Link>
              </div>
            </form>
          )}
        </Formik>
      </div>
    </>
  );
}

export default SignUpForm;
